<template>
    <div class="tw-w-full tw-grid tw-grid-cols-12 tw-gap-6">
        <div class="tw-col-span-12 lg:tw-col-start-2 lg:tw-col-end-12 xl:tw-col-start-3 xl:tw-col-end-11">
            <!-- Header Section -->
            <div v-if="true" class="tw-p-6 tw-pb-0">
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
                    <div>
                        <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">
                            {{ $t('account.profile.title', 'My Account') }}
                        </h1>
                        <p class="tw-text-gray-600">
                            {{ $t('account.profile.description', 'Manage your profile information and preferences') }}
                        </p>
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <a-button 
                            intent="outline" 
                            @click="resetForm"
                            :disabled="isSubmitting || !hasChanges"
                            left-icon="solar:refresh-linear"
                        >
                            {{ $t('account.profile.reset', 'Reset') }}
                        </a-button>
                        <a-button 
                            intent="primary" 
                            @click="saveProfile"
                            :loading="isSubmitting"
                            :disabled="!hasChanges || isSubmitting"
                            left-icon="solar:check-circle-bold"
                        >
                            {{ $t('account.profile.save', 'Save Changes') }}
                        </a-button>
                    </div>
                </div>

                <!-- Success/Error Messages -->
                <div v-if="message" class="tw-mt-4">
                    <div :class="[
                        'tw-p-3 tw-rounded-lg tw-border tw-text-sm',
                        message.type === 'success' 
                            ? 'tw-bg-green-50 tw-border-green-200 tw-text-green-800' 
                            : 'tw-bg-red-50 tw-border-red-200 tw-text-red-800'
                    ]">
                        {{ message.text }}
                    </div>
                </div>
            </div>

            <!-- Profile Form -->
            <div class="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
                <!-- Form Content -->
                <div class="tw-p-6">
                    <!-- Loading State -->
                    <div v-if="loading" class="tw-flex tw-items-center tw-justify-center tw-py-12">
                        <div class="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-primary-600"></div>
                        <span class="tw-ml-3 tw-text-gray-600">{{ $t('account.profile.loading', 'Loading profile...') }}</span>
                    </div>

                    <!-- Form Content -->
                    <div v-else class="tw-space-y-8">
                        <!-- Personal Information Section -->
                        <div class="tw-space-y-6x tw-mt-0">
                            <div class="tw-bg-primary-50 tw-border tw-border-primary-200 tw-rounded-lg tw-p-4 tw-hidden">
                                <div class="tw-flex tw-items-start tw-gap-3">
                                    <icon icon="solar:user-speak-bold-duotone" class="tw-text-primary-600 tw-text-lg tw-mt-0.5 tw-flex-shrink-0" />
                                    <div>
                                        <h3 class="tw-text-lg tw-font-medium tw-text-primary-800 tw-mb-2">
                                            {{ $t('account.profile.personal.title', 'Personal Information') }}
                                        </h3>
                                        <p class="tw-text-sm tw-text-primary-700">
                                            {{ $t('account.profile.personal.description', 'Update your basic profile information') }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="tw-space-y-6">
                                <!-- Personal Information Header -->
                                <h4 class="tw-text-base tw-font-medium tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
                                    <icon icon="solar:user-bold-duotone" class="tw-text-primary-600 tw-text-sm" />
                                    <span>{{ $t('account.profile.personal.main_info', 'Main Information') }}</span>
                                </h4>

                                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                                    <!-- Full Name -->
                                    <FormField
                                        v-model="formData.full_name"
                                        type="text"
                                        :label="$t('account.profile.personal.full_name', 'Full Name')"
                                        :placeholder="$t('account.profile.personal.full_name_placeholder', 'Enter your full name')"
                                        left-icon="solar:user-bold-duotone"
                                        required
                                        :error="errors.full_name"
                                        :hide-errors="!errors.full_name"
                                    />

                                    <!-- Contact Email -->
                                    <FormField
                                        v-model="formData.contact_email"
                                        type="email"
                                        :label="$t('account.profile.personal.contact_email', 'Contact Email')"
                                        :placeholder="$t('account.profile.personal.contact_email_placeholder', 'Enter your contact email')"
                                        left-icon="solar:letter-bold-duotone"
                                        required
                                        :error="errors.contact_email"
                                        :hide-errors="!errors.contact_email"
                                    />
                                </div>

                                <!-- Profile Image Upload -->
                                <div class="tw-max-w-md">
                                    <FormFileUpload
                                        v-model="formData.profile_image"
                                        :label="$t('account.profile.personal.profile_image', 'Profile Image')"
                                        :placeholder="$t('account.profile.personal.profile_image_placeholder', 'Upload a profile picture')"
                                        :help="$t('account.profile.personal.profile_image_help', 'Recommended size: 200x200 pixels, Max: 2MB')"
                                        left-icon="solar:gallery-bold-duotone"
                                        accept="image/*"
                                        :error="errors.profile_image"
                                        :hide-errors="!errors.profile_image"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- About & Address Section -->
                        <div class="tw-space-y-6">
                            <div class="tw-bg-primary-50 tw-border tw-border-primary-200 tw-rounded-lg tw-p-4 tw-hidden">
                                <div class="tw-flex tw-items-start tw-gap-3">
                                    <icon icon="solar:map-point-search-bold-duotone" class="tw-text-primary-600 tw-text-lg tw-mt-0.5 tw-flex-shrink-0" />
                                    <div>
                                        <h3 class="tw-text-lg tw-font-medium tw-text-primary-800 tw-mb-2">
                                            {{ $t('account.profile.about.title', 'About & Address') }}
                                        </h3>
                                        <p class="tw-text-sm tw-text-primary-700">
                                            {{ $t('account.profile.about.description', 'Tell us about yourself and your location') }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="tw-space-y-6">
                                <!-- About -->
                                <div>
                                    <FormField
                                        v-model="formData.about"
                                        type="textarea"
                                        :label="$t('account.profile.about.about_label', 'About Me')"
                                        :placeholder="$t('account.profile.about.about_placeholder', 'Tell us about yourself...')"
                                        rows="3"
                                        :error="errors.about"
                                        :hide-errors="!errors.about"
                                    />
                                </div>

                                <!-- Address Section -->
                                <div class="tw-space-y-4">
                                    <h4 class="tw-text-base tw-font-medium tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
                                        <icon icon="solar:map-point-bold-duotone" class="tw-text-primary-600 tw-text-sm" />
                                        <span>{{ $t('account.profile.about.address_section', 'Address Information') }}</span>
                                    </h4>
                                    
                                    <!-- Street Address -->
                                    <FormField
                                        v-model="formData.address.street"
                                        type="text"
                                        :label="$t('account.profile.about.street_address', 'Street Address')"
                                        :placeholder="$t('account.profile.about.street_address_placeholder', 'Enter your street address')"
                                        left-icon="solar:map-point-bold-duotone"
                                        :error="errors.street"
                                        :hide-errors="!errors.street"
                                    />

                                    <!-- City and State/Province -->
                                    <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                                        <FormField
                                            v-model="formData.address.city"
                                            type="text"
                                            :label="$t('account.profile.about.city', 'City')"
                                            :placeholder="$t('account.profile.about.city_placeholder', 'Enter your city')"
                                            :error="errors.city"
                                            :hide-errors="!errors.city"
                                        />
                                        <FormField
                                            v-model="formData.address.state"
                                            type="text"
                                            :label="$t('account.profile.about.state', 'State/Province')"
                                            :placeholder="$t('account.profile.about.state_placeholder', 'Enter your state')"
                                            :error="errors.state"
                                            :hide-errors="!errors.state"
                                        />
                                    </div>

                                    <!-- Country and Postal Code -->
                                    <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                                        <FormVueSelect
                                            v-model="formData.address.country_id"
                                            :options="countries"
                                            :label="$t('account.profile.about.country', 'Country')"
                                            :placeholder="$t('account.profile.about.country_placeholder', 'Select your country')"
                                            option-value="id"
                                            option-label="name"
                                            left-icon="solar:map-point-bold-duotone"
                                            :error="errors.country_id"
                                            :hide-errors="!errors.country_id"
                                        />
                                        <FormField
                                            v-model="formData.address.postal_code"
                                            type="text"
                                            :label="$t('account.profile.about.postal_code', 'Postal Code')"
                                            :placeholder="$t('account.profile.about.postal_code_placeholder', 'Enter postal code')"
                                            :error="errors.postal_code"
                                            :hide-errors="!errors.postal_code"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Social Media Section -->
                        <div class="tw-space-y-6">
                            <div class="tw-bg-primary-50 tw-border tw-border-primary-200 tw-rounded-lg tw-p-4 tw-hidden">
                                <div class="tw-flex tw-items-start tw-gap-3">
                                    <icon icon="solar:globus-bold-duotone" class="tw-text-primary-600 tw-text-lg tw-mt-0.5 tw-flex-shrink-0" />
                                    <div>
                                        <h3 class="tw-text-lg tw-font-medium tw-text-primary-800 tw-mb-2">
                                            {{ $t('account.profile.social.title', 'Social Media & Links') }}
                                        </h3>
                                        <p class="tw-text-sm tw-text-primary-700">
                                            {{ $t('account.profile.social.description', 'Connect your social media profiles and website') }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="tw-space-y-6">
                                <!-- Social Media Header -->
                                <h4 class="tw-text-base tw-font-medium tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
                                    <icon icon="solar:globus-bold-duotone" class="tw-text-primary-600 tw-text-sm" />
                                    <span>{{ $t('account.profile.social.social_links', 'Social Media Links') }}</span>
                                </h4>

                                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                                    <!-- LinkedIn -->
                                    <FormField
                                        v-model="formData.linkedin_url"
                                        type="url"
                                        :label="$t('account.profile.social.linkedin', 'LinkedIn')"
                                        :placeholder="$t('account.profile.social.linkedin_placeholder', 'https://linkedin.com/in/username')"
                                        left-icon="ri:linkedin-fill"
                                        :error="errors.linkedin_url"
                                        :hide-errors="!errors.linkedin_url"
                                    />

                                    <!-- Instagram -->
                                    <FormField
                                        v-model="formData.instagram_url"
                                        type="url"
                                        :label="$t('account.profile.social.instagram', 'Instagram')"
                                        :placeholder="$t('account.profile.social.instagram_placeholder', 'https://instagram.com/username')"
                                        left-icon="proicons:instagram"
                                        :error="errors.instagram_url"
                                        :hide-errors="!errors.instagram_url"
                                    />

                                    <!-- Twitter -->
                                    <FormField
                                        v-model="formData.twitter_url"
                                        type="url"
                                        :label="$t('account.profile.social.twitter', 'Twitter')"
                                        :placeholder="$t('account.profile.social.twitter_placeholder', 'https://twitter.com/username')"
                                        left-icon="hugeicons:new-twitter"
                                        :error="errors.twitter_url"
                                        :hide-errors="!errors.twitter_url"
                                    />

                                    <!-- Facebook -->
                                    <FormField
                                        v-model="formData.facebook_url"
                                        type="url"
                                        :label="$t('account.profile.social.facebook', 'Facebook')"
                                        :placeholder="$t('account.profile.social.facebook_placeholder', 'https://facebook.com/username')"
                                        left-icon="ri:facebook-fill"
                                        :error="errors.facebook_url"
                                        :hide-errors="!errors.facebook_url"
                                    />

                                    <!-- YouTube -->
                                    <FormField
                                        v-model="formData.youtube_url"
                                        type="url"
                                        :label="$t('account.profile.social.youtube', 'YouTube')"
                                        :placeholder="$t('account.profile.social.youtube_placeholder', 'https://youtube.com/@username')"
                                        left-icon="famicons:logo-youtube"
                                        :error="errors.youtube_url"
                                        :hide-errors="!errors.youtube_url"
                                    />

                                    <!-- GitHub -->
                                    <FormField
                                        v-model="formData.github_url"
                                        type="url"
                                        :label="$t('account.profile.social.github', 'GitHub')"
                                        :placeholder="$t('account.profile.social.github_placeholder', 'https://github.com/username')"
                                        left-icon="mdi:github"
                                        :error="errors.github_url"
                                        :hide-errors="!errors.github_url"
                                    />

                                    <!-- Website -->
                                    <FormField
                                        v-model="formData.website_url"
                                        type="url"
                                        :label="$t('account.profile.social.website', 'Website')"
                                        :placeholder="$t('account.profile.social.website_placeholder', 'https://yourwebsite.com')"
                                        left-icon="solar:globe-bold-duotone"
                                        :error="errors.website_url"
                                        :hide-errors="!errors.website_url"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useAuthStore } from '@/stores';
import FormField from '@/components/form/FormField.vue';
import FormFileUpload from '@/components/form/FormFileUpload.vue';
import FormVueSelect from '@/components/form/FormVueSelect.vue';
import onboardingApi from '@/api/onboarding';
import appApi from '@/api/app';
import useAlert from '@/composables/useAlert';

const authStore = useAuthStore();
const user = authStore.user;

// Component state
const loading = ref(false);
const isSubmitting = ref(false);
const message = ref(null);
const originalFormData = ref(null);

// Form data structure (removed language field)
const formData = reactive({
    // Personal Information
    full_name: '',
    profile_image: null,
    contact_email: '',
    
    // About & Address
    about: '',
    address: {
        street: '',
        city: '',
        state: '',
        country_id: '',
        postal_code: ''
    },
    
    // Social Media
    linkedin_url: '',
    instagram_url: '',
    twitter_url: '',
    facebook_url: '',
    youtube_url: '',
    github_url: '',
    website_url: ''
});

// Form validation errors
const errors = reactive({});

// Countries data (fetched from API)
const countries = ref([]);

// Computed properties
const hasChanges = computed(() => {
    if (!originalFormData.value) return false;
    
    return JSON.stringify(formData) !== JSON.stringify(originalFormData.value);
});

// Methods
const fetchCountries = async () => {
    try {
        const response = await appApi.forms.countries();
        if (response.data) {
            countries.value = response.data;
        }
    } catch (error) {
        console.error('Error fetching countries:', error);
        // Fallback to empty array if API fails
        countries.value = [];
    }
};

const fetchUserProfile = async () => {
    loading.value = true;
    try {
        const response = await onboardingApi.get();
        if (response.data && response.data.profile) {
            Object.assign(formData, response.data.profile);
            // Store original data for change detection
            originalFormData.value = JSON.parse(JSON.stringify(formData));
        }
    } catch (error) {
        console.error('Error fetching profile:', error);
        useAlert('Failed to load profile data. Please try again.', 'error');
    } finally {
        loading.value = false;
    }
};

const saveProfile = async () => {
    if (!hasChanges.value) return;
    
    isSubmitting.value = true;
    message.value = null;
    
    try {
        // Create FormData object for proper file upload handling
        const submitFormData = new FormData();
        
        // Add basic fields (removed language)
        submitFormData.append('full_name', formData.full_name);
        submitFormData.append('contact_email', formData.contact_email);
        submitFormData.append('about', formData.about);
        
        // Add profile image if exists and is a File object
        if (formData.profile_image && formData.profile_image instanceof File) {
            submitFormData.append('profile_image', formData.profile_image);
        }
        
        // Add address fields (flatten the nested object)
        if (formData.address) {
            Object.keys(formData.address).forEach(key => {
                if (formData.address[key]) {
                    submitFormData.append(key, formData.address[key]);
                }
            });
        }
        
        // Add social media URLs
        const socialFields = ['linkedin_url', 'instagram_url', 'twitter_url', 'facebook_url', 'youtube_url', 'github_url', 'website_url'];
        socialFields.forEach(field => {
            if (formData[field]) {
                submitFormData.append(field, formData[field]);
            }
        });
        
        const response = await onboardingApi.set(submitFormData);
        
        // Update original data after successful save
        originalFormData.value = JSON.parse(JSON.stringify(formData));
        
        // Show success message
        message.value = {
            type: 'success',
            text: 'Profile updated successfully!'
        };
        
        // Update user in auth store if needed
        if (response.data && response.data.profile) {
            authStore.setUser({ ...user, ...response.data.profile });
        }
        
        // Clear any existing errors
        Object.keys(errors).forEach(key => delete errors[key]);
        
    } catch (error) {
        console.error('Error saving profile:', error);
        
        if (error.response && error.response.data && error.response.data.errors) {
            // Handle validation errors
            Object.keys(error.response.data.errors).forEach(key => {
                errors[key] = error.response.data.errors[key][0];
            });
            
            message.value = {
                type: 'error',
                text: 'Please fix the errors below and try again.'
            };
        } else {
            message.value = {
                type: 'error',
                text: 'Failed to save profile. Please try again.'
            };
        }
    } finally {
        isSubmitting.value = false;
    }
};

const resetForm = () => {
    if (originalFormData.value) {
        Object.assign(formData, JSON.parse(JSON.stringify(originalFormData.value)));
        Object.keys(errors).forEach(key => delete errors[key]);
        message.value = null;
    }
};

const clearMessage = () => {
    message.value = null;
};

// Watch for message changes to auto-clear after 5 seconds
watch(message, (newMessage) => {
    if (newMessage && newMessage.type === 'success') {
        setTimeout(clearMessage, 5000);
    }
});

// Lifecycle
onMounted(async () => {
    // Fetch countries and user profile in parallel
    await Promise.all([
        fetchCountries(),
        fetchUserProfile()
    ]);
});
</script>

<style scoped>
/* Add any specific styles if needed */
</style>